import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Gift, Copy, Download, Plus, Check, ArrowLeft, Search, Loader2, Mail, Users,
} from "lucide-react";

export default function InstructorCompCodes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(20);
  const [label, setLabel] = useState("");
  const [generating, setGenerating] = useState(false);
  const [latestBatch, setLatestBatch] = useState(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, available, redeemed

  const PUBLIC_ORIGIN = window.location.origin;

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/comp-codes");
      setData(d);
    } catch {
      toast.error("Failed to load codes");
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    if (count < 1 || count > 100) {
      toast.error("Count must be 1-100");
      return;
    }
    setGenerating(true);
    try {
      const { data: d } = await api.post("/admin/comp-codes", {
        count: parseInt(count, 10),
        label: label.trim() || null,
      });
      setLatestBatch(d);
      toast.success(`Generated ${d.count} complimentary codes`);
      setLabel("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const downloadCsv = (codes, filename) => {
    const rows = [
      ["code", "redeem_url", "status", "redeemed_by_email", "redeemed_at", "batch_label"],
      ...codes.map((c) => [
        c.code,
        `${PUBLIC_ORIGIN}/redeem?code=${c.code}`,
        c.redeemed_at ? "redeemed" : "available",
        c.redeemed_by_email || "",
        c.redeemed_at || "",
        c.batch_label || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = (data?.codes || []).filter((c) => {
    const matchSearch = !filter ||
      c.code.toLowerCase().includes(filter.toLowerCase()) ||
      (c.batch_label || "").toLowerCase().includes(filter.toLowerCase()) ||
      (c.redeemed_by_email || "").toLowerCase().includes(filter.toLowerCase());
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "available" && !c.redeemed_at) ||
      (statusFilter === "redeemed" && c.redeemed_at);
    return matchSearch && matchStatus;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="comp-codes-page">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="overline mb-3"><span className="red-bar" />Instructor · Complimentary Access</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter">
              Complimentary access codes
            </h1>
            <p className="text-zinc-700 mt-2 max-w-2xl leading-relaxed text-sm">
              Generate free redemption codes to give students access without payment. Each code unlocks
              the full course for one student. Share the code (or the redemption link) however you like.
            </p>
          </div>
          <Link to="/instructor" className="text-sm text-zinc-600 hover:text-red-600" data-testid="back-to-instructor">
            <ArrowLeft className="w-4 h-4 inline mr-1" /> Back to dashboard
          </Link>
        </div>

        {/* Stats */}
        {data && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <Stat label="Total generated" value={data.total} icon={<Gift className="w-4 h-4" />} />
            <Stat label="Redeemed" value={data.redeemed} icon={<Users className="w-4 h-4" />} accent="green" />
            <Stat label="Available" value={data.available} icon={<Mail className="w-4 h-4" />} accent="red" />
          </div>
        )}

        {/* Generator */}
        <div className="card-tech p-6 mb-6" data-testid="comp-codes-generator">
          <div className="flex items-start gap-2 mb-4">
            <Plus className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h2 className="font-heading text-lg font-bold tracking-tight">Generate new batch</h2>
              <p className="text-xs text-zinc-600">Codes use the format <code className="bg-zinc-100 px-1">APMGIFT-XXXXX-XXXXX</code> and never expire until redeemed.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-12 gap-3">
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 block mb-1">Quantity</label>
              <input
                type="number"
                min="1"
                max="100"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="w-full border-2 border-zinc-200 px-3 py-2 text-sm focus:border-red-600 outline-none"
                data-testid="comp-codes-count"
              />
            </div>
            <div className="sm:col-span-6">
              <label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 block mb-1">Batch label (optional)</label>
              <input
                type="text"
                placeholder="e.g. Bronx HS 2026 cohort"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full border-2 border-zinc-200 px-3 py-2 text-sm focus:border-red-600 outline-none"
                data-testid="comp-codes-label"
              />
            </div>
            <div className="sm:col-span-3 flex items-end">
              <button
                onClick={generate}
                disabled={generating}
                className="btn-primary w-full justify-center"
                data-testid="comp-codes-generate-btn"
              >
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Plus className="w-4 h-4" /> Generate {count} codes</>}
              </button>
            </div>
          </div>
        </div>

        {/* Latest batch result */}
        {latestBatch && (
          <div className="card-tech p-5 mb-6 border-2 border-green-300 bg-green-50/30" data-testid="latest-batch">
            <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
              <div>
                <div className="text-xs font-mono text-green-700 uppercase tracking-widest">✓ Just generated</div>
                <div className="font-heading text-lg font-bold tracking-tight">
                  {latestBatch.count} codes {latestBatch.label && <span className="text-zinc-500 font-normal">· {latestBatch.label}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => copyText(latestBatch.codes.map((c) => c.code).join("\n"))}
                  className="btn-outline text-xs"
                  data-testid="copy-batch-codes"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy all codes
                </button>
                <button
                  onClick={() => copyText(latestBatch.codes.map((c) => `${PUBLIC_ORIGIN}/redeem?code=${c.code}`).join("\n"))}
                  className="btn-outline text-xs"
                  data-testid="copy-batch-links"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy redeem links
                </button>
                <button
                  onClick={() => downloadCsv(latestBatch.codes, `apm-comp-codes-${Date.now()}.csv`)}
                  className="btn-primary text-xs"
                  data-testid="download-batch-csv"
                >
                  <Download className="w-3.5 h-3.5" /> Download CSV
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {latestBatch.codes.map((c) => (
                <div key={c.code} className="font-mono text-xs bg-white border border-zinc-200 px-2.5 py-1.5 flex items-center justify-between gap-2">
                  <span className="truncate">{c.code}</span>
                  <button onClick={() => copyText(c.code)} className="text-zinc-400 hover:text-red-600 flex-shrink-0">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 font-mono mt-3">Each redemption link: <code>{PUBLIC_ORIGIN}/redeem?code=APMGIFT-...</code></p>
          </div>
        )}

        {/* Filters + table */}
        <div className="card-tech p-5">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h2 className="font-heading text-lg font-bold tracking-tight">All complimentary codes</h2>
            {data?.codes?.length > 0 && (
              <button
                onClick={() => downloadCsv(filtered, `apm-comp-codes-all-${Date.now()}.csv`)}
                className="btn-outline text-xs"
                data-testid="download-all-csv"
              >
                <Download className="w-3.5 h-3.5" /> Export filtered ({filtered.length})
              </button>
            )}
          </div>

          {/* Filter row */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                placeholder="Search by code, label, or redeemer email..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border-2 border-zinc-200 focus:border-red-600 outline-none"
                data-testid="comp-codes-search"
              />
            </div>
            <div className="flex gap-1">
              {["all", "available", "redeemed"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border-2 transition-colors ${statusFilter === s ? "bg-red-600 text-white border-red-600" : "bg-white text-zinc-700 border-zinc-200 hover:border-red-600"}`}
                  data-testid={`comp-codes-filter-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-12 text-zinc-500 font-mono text-sm">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm" data-testid="comp-codes-empty">
              {data?.codes?.length === 0
                ? "No codes generated yet. Use the form above to create your first batch."
                : "No codes match your filters."}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm" data-testid="comp-codes-table">
                <thead className="border-b-2 border-zinc-200">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3">Batch</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Redeemed by</th>
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.code} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`comp-code-row-${c.code}`}>
                      <td className="py-2.5 pr-3 font-mono text-xs whitespace-nowrap">{c.code}</td>
                      <td className="py-2.5 pr-3 text-xs text-zinc-600">{c.batch_label || "—"}</td>
                      <td className="py-2.5 pr-3">
                        {c.redeemed_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-[10px] font-mono uppercase tracking-wider font-bold">
                            <Check className="w-3 h-3" /> Redeemed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-200 text-zinc-800 text-[10px] font-mono uppercase tracking-wider font-bold">
                            Available
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-zinc-700">
                        {c.redeemed_by_email ? (
                          <div>
                            <div className="font-medium">{c.redeemed_by_name || c.redeemed_by_email}</div>
                            {c.redeemed_by_name && <div className="text-zinc-500 text-[11px]">{c.redeemed_by_email}</div>}
                          </div>
                        ) : "—"}
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-zinc-500 font-mono whitespace-nowrap">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => copyText(c.code)}
                            title="Copy code"
                            className="text-zinc-500 hover:text-red-600 p-1"
                            data-testid={`copy-code-${c.code}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => copyText(`${PUBLIC_ORIGIN}/redeem?code=${c.code}`)}
                            title="Copy redemption link"
                            className="text-zinc-500 hover:text-red-600 p-1 text-[10px] font-mono uppercase tracking-wider px-2 border border-zinc-200 hover:border-red-600"
                            data-testid={`copy-link-${c.code}`}
                          >
                            Link
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const Stat = ({ label, value, icon, accent }) => (
  <div className="card-tech p-4">
    <div className={`flex items-center gap-2 text-xs font-mono uppercase tracking-widest mb-1 ${accent === "green" ? "text-green-700" : accent === "red" ? "text-red-600" : "text-zinc-500"}`}>
      {icon}
      {label}
    </div>
    <div className="font-heading text-3xl font-bold tracking-tighter">{value}</div>
  </div>
);
